import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import AircraftInline from '@components/AircraftOverlay/AircraftInline';
import AircraftInlineClouds from '@components/AircraftOverlay/AircraftInlineClouds';
import { CSSBlockPage } from '@core/constants';
import { getAircraft } from '@core/services/networkService/_aircraft';
import { RouteParams } from '@core/services/routingService';
import { nscRequest } from '@helpers/app.helper';
import { Aircraft } from '@models/aircraft.model';
import { Card } from '@shadCN/ui/card';

const AircraftView: React.FC = () => {
  const CSSBlock = 'aircraft-view';
  const { [RouteParams.AIRCRAFT_ID]: aircraftID } = useParams();
  const [aircraft, setAircraft] = useState<Aircraft>();

  useEffect(() => {
    if (aircraftID)
      nscRequest(getAircraft(aircraftID)).then(o => {
        setAircraft(o);
      });
  }, [aircraftID]);

  const card = (title: string, value: string) => {
    return (
      <Card className="p-6">
        <div className="text-xl font-light">{title}</div>
        <div className="flex md:flex-wrap flex-col gap-2">
          <div className="">{value}</div>
        </div>
      </Card>
    );
  };

  const cardKeyVal = (
    title: string,
    values: { key: string; value: string }[]
  ) => {
    return (
      <Card className="p-6">
        <div className="text-xl font-light">{title}</div>
        <div className="flex md:flex-wrap flex-col gap-2">
          {values.map(({ key, value }) => keyVal(key, value))}
        </div>
      </Card>
    );
  };

  const keyVal = (key: string, value: string) => {
    return (
      <div className="flex justify-between">
        <div className="font-medium capitalize" style={{ opacity: 0.5 }}>
          {key}
        </div>
        <div className="" style={{ opacity: 0.8 }}>
          {value}
        </div>
      </div>
    );
  };

  return aircraft ? (
    <div className={`${CSSBlockPage} ${CSSBlock}`}>
      <div
        className={`text-3xl font-extralight text-center mt-4`}
        style={{ opacity: 0.7 }}>
        {aircraft.model}
      </div>
      <div
        className={`text-md font-semibold text-center mt-2 uppercase tracking-widest`}
        style={{ opacity: 0.5 }}>
        {aircraft.manufacturer}
      </div>
      <div
        style={{
          zIndex: 0,
        }}>
        <AircraftInlineClouds />
      </div>
      <div
        style={{
          margin: '-100px auto',
          width: 'fit-content',
          display: 'flex',
          justifyContent: 'center',
          transform: 'scale(1.3)',
          pointerEvents: 'none',
        }}>
        <AircraftInline />
      </div>
      <div
        className={`${CSSBlockPage}__content grid-cols-1 md:grid-cols-2 grid gap-4 `}
        style={{ width: '100%' }}>
        {cardKeyVal('Airframe', [
          {
            key: 'Total Time Since New',
            value: `${aircraft.airframe.totalTimeSinceNew} hours`,
          },
          {
            key: 'Landings Since New',
            value: `${aircraft.airframe.landingsSinceNew}`,
          },
          {
            key: 'Air Worthiness Date',
            value: aircraft.airframe.airWorthnessDate.toDateString(),
          },
          {
            key: 'Maintenance Tracking',
            value: aircraft.airframe.maintenanceTracking,
          },
          { key: 'Program Coverage', value: aircraft.airframe.programCoverage },
        ])}
        {cardKeyVal('Engines', [
          { key: 'Manufacturer', value: aircraft.engines.manufacturer },
          { key: 'Model', value: aircraft.engines.model },
          { key: 'Programn Coverage', value: aircraft.engines.programCoverage },
        ])}
        {card('Avionics', aircraft.avionics)}
        {cardKeyVal('APU', [
          { key: 'Manufacturer', value: aircraft.apu.manufacturer },
          { key: 'Model', value: aircraft.apu.model },
          { key: 'Serial Number', value: aircraft.apu.serialNumber },
          {
            key: 'Time Since New',
            value: `${aircraft.apu.timeSinceNew} hours`,
          },
          { key: 'Program Coverage', value: aircraft.apu.programCoverage },
        ])}
        {cardKeyVal('Connectivity & Entertainment', [
          {
            key: 'Internet',
            value: aircraft.connectivityAndEntertainment.internet,
          },
          {
            key: 'Phone System',
            value: aircraft.connectivityAndEntertainment.phone,
          },
          {
            key: 'Cabin Systems',
            value: aircraft.connectivityAndEntertainment.display,
          },
        ])}
        {cardKeyVal(
          'Aircraft Service Changes',
          aircraft.aircraftServiceChanges.map(o => ({
            key: Object.keys(o)[0],
            value: Object.values(o)[0],
          }))
        )}
        {cardKeyVal('Interior', [
          {
            key: 'Number of Passengers',
            value: `${aircraft.interior.noOfPassengers}`,
          },
          {
            key: 'Gallery Location',
            value: aircraft.interior.galleryLocation,
          },
          {
            key: 'Forward Cabin Configuration',
            value: aircraft.interior.forwardCabinConfiguration,
          },
          {
            key: 'Mid Cabin Configuration',
            value: aircraft.interior.midCabinConfiguration,
          },
          {
            key: 'AFT Cabin Configuration',
            value: aircraft.interior.aftCabinConfiguration,
          },
          {
            key: 'Lavatory',
            value: aircraft.interior.lavatory,
          },
          {
            key: 'Crew Rest Area',
            value: aircraft.interior.crewRestArea,
          },
          {
            key: 'Jump Seat',
            value: aircraft.interior.jumpSeat,
          },
          {
            key: 'Last Refurbishment Date',
            value: aircraft.interior.lastRefurbishmentDate.toDateString(),
          },
        ])}
        {cardKeyVal('Exterior', [
          { key: 'Base Paint Color', value: aircraft.exterior.basePaintColor },
          { key: 'Stripe Color', value: aircraft.exterior.stripeColor },
          {
            key: 'Last Refurbishment Date',
            value: aircraft.exterior.lastRefurbishmentDate.toDateString(),
          },
        ])}
        {cardKeyVal('Major Inspection Status', [
          {
            key: 'Month 12',
            value: aircraft.majorInspectionStatus?.month12 ?? '',
          },
          {
            key: 'Month 24',
            value: aircraft.majorInspectionStatus?.month24 ?? '',
          },
          {
            key: 'Month 48',
            value: aircraft.majorInspectionStatus?.month48 ?? '',
          },
          {
            key: 'Month 96',
            value: aircraft.majorInspectionStatus?.month96 ?? '',
          },
        ])}
      </div>
    </div>
  ) : (
    <></>
  );
};

export default AircraftView;
